import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import { ChatFireService } from '../../../gen2ai/chatfire.service';
import { AuthenticationService } from '../../../clari-auth/authentication.service';
import { UserProfile } from '../../../interfaces-types/user.interface';
import { Conversation } from '../../../models/chat.model';
import { ReasoningService, ReasoningLevel } from '../../../services/reasoning.service';
import { ClarityCoachService, CoachEntry } from '../../../services/clarity-coach.service';
import { AiCatalogComponent } from '../../../authentication/dialogs/ai-catalog/ai-catalog.component';

type BusinessMenuOpenStyle = 'fullscreen' | 'drawer-left' | 'drawer-right';
type BusinessMenuFabCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

@Component({
  selector: 'app-business-menu',
  templateUrl: './business-menu.component.html',
  styleUrls: ['./business-menu.component.scss']
})
export class BusinessMenuComponent implements OnInit, OnDestroy {
  /** Floating button corner - DEFAULT TO BOTTOM-RIGHT */
  @Input() fabCorner: BusinessMenuFabCorner = 'bottom-right';

  /**
   * How the menu panel positions inside the full-screen overlay.
   * - fullscreen: centered panel (overlay still 100vw/100vh)
   * - drawer-left/right: panel aligned to a side (overlay still 100vw/100vh)
   */
  @Input() openStyle: BusinessMenuOpenStyle = 'fullscreen';

  /** Show/hide reasoning dropdown area (kept compatible with your existing UI) */
  @Input() showReasoningDropdown = false;

  /** Emit whenever the menu opens/closes so Layout can sync shared state */
  @Output() openChange = new EventEmitter<boolean>();

  isOpen = false;
  popPlayEnabled = false;
  popPlaySize: 'small' | 'large' = 'small';

  fabStyle: Record<string, string> = {};
  popPlayStyle: Record<string, string> = {
    right: '18px',
    bottom: '92px'
  };
  private fabPosition: { x: number; y: number } | null = null;
  private popPlayPosition: { x: number; y: number } | null = null;

  // Section accordion state
  expandedSections: Record<string, boolean> = {
    coach: true // Always expanded
  };

  // Model usage tracking per route
  modelUsageByRoute: Record<string, Record<string, number>> = {};

  /* Keep your existing section routing logic */
  sectionRoutes: Record<string, string[]> = {
    coach: []
  };

  // Authentication state
  currentUser: UserProfile | null = null;
  isLoggedIn = false;

  // Conversation history
  recentConversations: Conversation[] = [];
  loadingConversations = false;

  // Reasoning dropdown
  reasoningDropdownOpen = false;
  selectedReasoningLevel: ReasoningLevel = 'thinking';
  legacyModelsExpanded = false;

  // Coach log
  coachEntries: CoachEntry[] = [];
  sessionCoachEntries: CoachEntry[] = [];
  historicalCoachEntries: CoachEntry[] = [];
  costPerformancePreference = 40;
  selectedCoachIndex = 0;
  activeCoachEntry: CoachEntry | null = null;
  coachLegendModels: string[] = [];
  private historicalLoaded = false;
  private activeSessionId: string | null = null;

  private authSub?: Subscription;
  private loginSub?: Subscription;
  private reasoningSub?: Subscription;
  private routerSub?: Subscription;
  private coachSub?: Subscription;
  private coachHistorySub?: Subscription;
  private coachPreferenceSub?: Subscription;
  private conversationSub?: Subscription;
  private sessionSub?: Subscription;

  get selectedModelName(): string {
    const modelId = localStorage.getItem('lastSelectedModel') || 'deepseek';
    return this.getModelDisplayName(modelId);
  }

  private getModelDisplayName(modelId: string): string {
    const modelMap: Record<string, string> = {
      'chatgpt5': 'ChatGPT 5.2',
      'chatgpt5-mini': 'ChatGPT 5-mini',
      'chatgpt52': 'ChatGPT 5.2',
      'opus45': 'Opus 4.5',
      'sonnet45': 'Sonnet 4.5',
      'zai': 'GLM 4.7',
      'glm47': 'GLM 4.7',
      'gemini': 'Gemini Pro',
      'gemini3flash': 'Gemini 3 Flash',
      'deepseek': 'DeepSeek R1',
      'deepv3': 'DeepSeek V3.2',
      'grok41': 'Grok 4',
      'grok': 'Grok',
      'grokimage': 'Grok Image',
      'palmx': 'Palmyra X5',
      'palmyra': 'Palmyra X5',
      'cohere': 'Cohere R+',
      'o3': 'ChatGPT o3',
      'maverick': 'Maverick',
      'pixtralimage': 'Pixtral',
      'pixtral': 'Pixtral',
      'mistrallarge': 'Mistral Large',
      'mistral': 'Mistral Large',
      'haiku45': 'Haiku 4.5',
      'haiku': 'Haiku 4.5',
      'jamba': 'Jamba',
      'novapro': 'Nova Pro',
      'novalite': 'Nova Lite'
    };
    return modelMap[modelId] || 'AI Model';
  }

  constructor(
    private router: Router,
    private chatService: ChatFireService,
    private authService: AuthenticationService,
    private reasoningService: ReasoningService,
    private coachService: ClarityCoachService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Reasoning level subscription
    this.reasoningSub = this.reasoningService.reasoningLevel$.subscribe(level => {
      this.selectedReasoningLevel = level;
    });
    this.selectedReasoningLevel = this.reasoningService.getReasoningLevel();

    // Auth subscriptions
    this.authSub = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user) this.loadRecentConversations();
      else this.recentConversations = [];
    });

    this.loginSub = this.authService.isLoggedIn$.subscribe(isLoggedIn => {
      this.isLoggedIn = isLoggedIn;
      if (isLoggedIn) this.loadRecentConversations();
      else this.recentConversations = [];
    });

    this.coachSub = this.coachService.entries$.subscribe(entries => {
      this.sessionCoachEntries = entries;
      this.setCoachLegendModels();
      this.updateDisplayCoachEntries();
      this.updateModelUsageTracking();

      if (this.sessionCoachEntries.length < 10 && !this.historicalLoaded && this.activeSessionId) {
        this.historicalLoaded = true;
        void this.coachService.loadHistoricalEntries(10).then(loaded => {
          if (!loaded) {
            this.historicalLoaded = false;
          }
        });
      }
    });

    this.coachHistorySub = this.coachService.historicalEntries$.subscribe(entries => {
      this.historicalCoachEntries = entries;
      this.updateDisplayCoachEntries();
    });

    this.sessionSub = this.chatService.sessionId$.subscribe(sessionId => {
      if (sessionId && sessionId !== this.activeSessionId) {
        this.activeSessionId = sessionId;
        this.historicalLoaded = false;
        this.historicalCoachEntries = [];
        this.updateDisplayCoachEntries();
        if (this.sessionCoachEntries.length < 10) {
          this.historicalLoaded = true;
          void this.coachService.loadHistoricalEntries(10).then(loaded => {
            if (!loaded) {
              this.historicalLoaded = false;
            }
          });
        }
      }
    });

    // Track model usage from conversations
    this.chatService.currentConversation$.subscribe(conv => {
      if (conv?.messages) {
        this.trackModelsFromConversation(conv);
      }
    });

    this.coachPreferenceSub = this.coachService.preference$.subscribe(value => {
      this.costPerformancePreference = value;
    });

    // Track model usage from conversations
    this.conversationSub = this.chatService.currentConversation$.subscribe(conv => {
      if (conv?.messages) {
        this.trackModelsFromConversation(conv);
      }
    });

    // Refresh conversation list after navigation
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isLoggedIn) {
          setTimeout(() => this.loadRecentConversations(), 400);
        }
      });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.loginSub?.unsubscribe();
    this.reasoningSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.coachSub?.unsubscribe();
    this.coachHistorySub?.unsubscribe();
    this.coachPreferenceSub?.unsubscribe();
    this.conversationSub?.unsubscribe();
    this.sessionSub?.unsubscribe();
    this.unlockBodyScroll();
  }

  // ======= Open/Close (public for Layout to call) =======
  toggleMenu(): void {
    this.isOpen ? this.closeMenu() : this.openMenu();
  }

  openMenu(): void {
    this.isOpen = true;
    this.openChange.emit(true);
    this.lockBodyScroll();
    // Always expand coach section
    this.expandedSections['coach'] = true;
    this.syncExpandedSectionsToRoute();
    // Scroll to top of coach log after menu opens
    setTimeout(() => this.scrollToCoachTop(), 100);
  }

  closeMenu(): void {
    this.isOpen = false;
    this.openChange.emit(false);
    this.reasoningDropdownOpen = false;
    this.legacyModelsExpanded = false;
    this.unlockBodyScroll();
  }

  onFabClick(): void {
    this.openLatestCoach();
  }

  togglePopPlay(): void {
    this.popPlayEnabled = !this.popPlayEnabled;
    if (this.popPlayEnabled) {
      this.requestPopPlayPermission();
      this.openLatestCoach();
    }
  }

  closePopPlay(): void {
    this.popPlayEnabled = false;
  }

  togglePopPlaySize(): void {
    this.popPlaySize = this.popPlaySize === 'large' ? 'small' : 'large';
  }

  private requestPopPlayPermission(): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined);
    }
  }

  private openLatestCoach(): void {
    this.selectedCoachIndex = 0;
    this.activeCoachEntry = this.coachEntries[0] ?? null;
    this.openMenu();
  }

  private lockBodyScroll(): void {
    if (typeof document === 'undefined') return;
    document.body.classList.add('business-menu-open');
  }

  private unlockBodyScroll(): void {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('business-menu-open');
  }

  // Close when clicking the dim area
  onBackdropClick(): void {
    this.closeMenu();
  }

  // Prevent closing when clicking inside the panel
  stopPropagation(evt: MouseEvent): void {
    evt.stopPropagation();
  }

  // ======= Sections =======
  toggleSection(sectionId: string): void {
    // Coach section should always stay expanded
    if (sectionId === 'coach') {
      return;
    }
    this.expandedSections[sectionId] = !this.expandedSections[sectionId];
  }

  isExpanded(sectionId: string): boolean {
    return !!this.expandedSections[sectionId];
  }

  isRouteInSection(sectionId: string): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return this.sectionRoutes[sectionId]?.some(route => currentUrl.startsWith(route)) ?? false;
  }

  private syncExpandedSectionsToRoute(): void {
    // Don't collapse coach - keep it expanded
    Object.keys(this.expandedSections).forEach(k => {
      if (k !== 'coach') {
        this.expandedSections[k] = false;
      }
    });

    const active = (Object.keys(this.sectionRoutes) as Array<keyof typeof this.sectionRoutes>).find(id =>
      this.isRouteInSection(id)
    );

    if (active && active !== 'coach') {
      this.expandedSections[active] = true;
    }
  }

  private scrollToCoachTop(): void {
    const coachSection = document.querySelector('.coach-section');
    if (coachSection) {
      const coachLog = coachSection.querySelector('.coach-log');
      if (coachLog) {
        coachLog.scrollTop = 0;
        coachSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  // ======= Conversations =======
  async loadRecentConversations(): Promise<void> {
    if (!this.isLoggedIn) {
      this.recentConversations = [];
      return;
    }

    try {
      this.loadingConversations = true;
      this.recentConversations = await firstValueFrom(this.chatService.getRecentConversations(5));
    } catch (err) {
      console.error('[BusinessMenu] Error loading conversations', err);
      this.recentConversations = [];
    } finally {
      this.loadingConversations = false;
    }
  }

  async loadConversation(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        await this.router.navigate(['/clarichat'], { queryParams: { conversationId } });
      }
    } catch (err) {
      console.error('[BusinessMenu] Error loading conversation', err);
    }
  }

  truncateTopic(topic: string): string {
    if (!topic) return 'Conversation';
    return topic.length <= 20 ? topic : topic.substring(0, 20) + '...';
  }

  truncateCoachText(text: string, max = 160): string {
    const trimmed = (text || '').trim();
    if (!trimmed) return '';
    return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max)}…`;
  }

  selectCoachEntry(index: number): void {
    if (index < 0 || index >= this.coachEntries.length) return;
    this.selectedCoachIndex = index;
    this.activeCoachEntry = this.coachEntries[index] ?? null;
  }

  navigateCoachEntry(delta: number): void {
    if (!this.coachEntries.length) return;
    const nextIndex = Math.min(
      Math.max(this.selectedCoachIndex + delta, 0),
      this.coachEntries.length - 1
    );
    this.selectCoachEntry(nextIndex);
  }

  getSavingsCount(entry: CoachEntry | null): number {
    if (!entry?.suggestions?.length) return 0;
    return entry.suggestions.filter(suggestion =>
      (suggestion.valueNote || '').toLowerCase().includes('save') ||
      (suggestion.valueNote || '').toLowerCase().includes('cheaper') ||
      (suggestion.reason || '').toLowerCase().includes('cost')
    ).length;
  }

  getPowerCount(entry: CoachEntry | null): number {
    if (!entry?.suggestions?.length) return 0;
    return entry.suggestions.filter(suggestion =>
      (suggestion.qualityNote || '').toLowerCase().includes('higher') ||
      (suggestion.qualityNote || '').toLowerCase().includes('better') ||
      (suggestion.reason || '').toLowerCase().includes('capable')
    ).length;
  }

  private setCoachLegendModels(): void {
    const nameCounts = new Map<string, number>();
    this.sessionCoachEntries.forEach(entry => {
      const name = entry.assistantModelName || entry.assistantModelId;
      if (!name) return;
      nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
    });

    this.coachLegendModels = Array.from(nameCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name]) => name);
  }

  private syncSelectedCoachEntry(): void {
    if (!this.coachEntries.length) {
      this.selectedCoachIndex = 0;
      this.activeCoachEntry = null;
      return;
    }
    if (this.selectedCoachIndex >= this.coachEntries.length) {
      this.selectedCoachIndex = 0;
    }
    this.activeCoachEntry = this.coachEntries[this.selectedCoachIndex] ?? null;
  }

  updatePreference(value: number): void {
    this.coachService.setPreference(Number(value));
  }

  // ======= Auth =======
async logout(): Promise<void> {
  try {
    await firstValueFrom(this.chatService.logout());
  } catch (err) {
    console.error('[BusinessMenu] Logout failed', err);
  } finally {
    this.currentUser = null;
    this.isLoggedIn = false;
    this.recentConversations = [];
    this.closeMenu();
    await this.router.navigate(['/auth/log-in'], { queryParams: { loggedOut: 'true' } });
  }
}

  // ======= Reasoning Dropdown =======
  toggleReasoningDropdown(): void {
    this.reasoningDropdownOpen = !this.reasoningDropdownOpen;
  }

  selectReasoningLevel(level: ReasoningLevel): void {
    this.reasoningService.setReasoningLevel(level);
    this.reasoningDropdownOpen = false;
  }

  getReasoningLabel(level: ReasoningLevel): string {
    const labels: Record<ReasoningLevel, string> = {
      auto: 'Auto',
      instant: 'Instant',
      thinking: 'Thinking',
      pro: 'Pro'
    };
    return labels[level] ?? 'Thinking';
  }

  toggleLegacyModels(): void {
    this.legacyModelsExpanded = !this.legacyModelsExpanded;
  }

  formatReasoningLevel(level: ReasoningLevel): string {
    if (!level) return 'Not available';
    return level
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  openAiCatalogDialog(): void {
    this.dialog.open(AiCatalogComponent, {
      panelClass: 'ai-catalog-dialog-panel',
      width: 'min(95vw, 1280px)',
      maxHeight: '90vh'
    });
    this.closeMenu();
  }

  openAddCredits(): void {
    console.log('Open credits page');
  }

  // ======= Model Usage Tracking =======
  private updateModelUsageTracking(): void {
    // Track models from coach entries
    this.sessionCoachEntries.forEach(entry => {
      if (entry.assistantModelName || entry.assistantModelId) {
        const modelName = entry.assistantModelName || entry.assistantModelId || '';
        const vendor = this.getVendorFromModel(modelName);
        if (vendor) {
          // Track by current route
          const currentRoute = this.router.url.split('?')[0];
          const routeKey = this.getRouteKeyForSection(currentRoute);
          if (routeKey) {
            this.incrementModelUsage(routeKey, vendor);
          }
        }
      }
    });
  }

  private trackModelsFromConversation(conv: Conversation): void {
    if (!conv?.messages) return;
    
    const currentRoute = this.router.url.split('?')[0];
    const routeKey = this.getRouteKeyForSection(currentRoute);
    if (!routeKey) return;

    conv.messages.forEach(msg => {
      if (msg.role === 'assistant' && (msg.modelDisplayName || msg.modelId)) {
        const modelName = msg.modelDisplayName || msg.modelId || '';
        const vendor = this.getVendorFromModel(modelName);
        if (vendor) {
          this.incrementModelUsage(routeKey, vendor);
        }
      }
    });
  }

  private incrementModelUsage(routeKey: string, vendor: string): void {
    if (!this.modelUsageByRoute[routeKey]) {
      this.modelUsageByRoute[routeKey] = {};
    }
    if (!this.modelUsageByRoute[routeKey][vendor]) {
      this.modelUsageByRoute[routeKey][vendor] = 0;
    }
    this.modelUsageByRoute[routeKey][vendor]++;
  }

  private getVendorFromModel(modelName: string): string | null {
    const name = modelName.toLowerCase();
    if (name.includes('gpt') || name.includes('chatgpt') || name.includes('o3') || name.includes('openai')) {
      return 'OpenAI';
    }
    if (name.includes('gemini') || name.includes('google')) {
      return 'Google';
    }
    if (name.includes('llama') || name.includes('meta') || name.includes('maverick') || name.includes('scout')) {
      return 'Meta';
    }
    if (name.includes('claude') || name.includes('opus') || name.includes('haiku') || name.includes('sonnet') || name.includes('anthropic')) {
      return 'Anthropic';
    }
    if (name.includes('grok') || name.includes('xai')) {
      return 'xAI';
    }
    if (name.includes('deepseek')) {
      return 'DeepSeek';
    }
    if (name.includes('cohere')) {
      return 'Cohere';
    }
    if (name.includes('glm') || name.includes('zai')) {
      return 'GLM';
    }
    if (name.includes('palmyra')) {
      return 'Palmyra';
    }
    if (name.includes('mistral')) {
      return 'Mistral';
    }
    if (name.includes('jamba')) {
      return 'Jamba';
    }
    return null;
  }

  private getRouteKeyForSection(route: string): string | null {
    // Map routes to section keys
    if (route.startsWith('/ai/clarity') || route.startsWith('/clarichat')) {
      return 'chat';
    }
    if (route.startsWith('/multi-ai/executive-chat') || route.startsWith('/multi-ai/exective-chat')) {
      return 'chat';
    }
    if (route.startsWith('/ai/compare-mode')) {
      return 'chat';
    }
    if (route.startsWith('/library/calendar') || route.startsWith('/library/search')) {
      return 'chat';
    }
    if (route.startsWith('/ai/studio')) {
      return 'create';
    }
    if (route.startsWith('/iq/genius') || route.startsWith('/iq/copy')) {
      return 'create';
    }
    if (route.startsWith('/clari-notes') || route.startsWith('/notes')) {
      return 'create';
    }
    if (route.startsWith('/iq/media-library')) {
      return 'create';
    }
    if (route.startsWith('/ai/resume-homie')) {
      return 'focus';
    }
    if (route.startsWith('/iq/lab')) {
      return 'focus';
    }
    if (route.startsWith('/iq/personality')) {
      return 'focus';
    }
    return null;
  }

  getModelBadge(sectionId: string): string | null {
    const usage = this.modelUsageByRoute[sectionId];
    if (!usage || Object.keys(usage).length === 0) {
      return null;
    }
    // Get the vendor with highest count
    const entries = Object.entries(usage).sort((a, b) => b[1] - a[1]);
    if (entries.length > 0) {
      const [vendor, count] = entries[0];
      return `${vendor} ${count}`;
    }
    return null;
  }

  // ======= Global listeners =======
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.closeMenu();
    }
  }

  private updateDisplayCoachEntries(): void {
    if (this.sessionCoachEntries.length >= 10) {
      this.coachEntries = this.sessionCoachEntries.slice(0, 10);
    } else {
      const seen = new Set(this.sessionCoachEntries.map(entry => entry.id));
      const merged = [
        ...this.sessionCoachEntries,
        ...this.historicalCoachEntries.filter(entry => !seen.has(entry.id))
      ];
      this.coachEntries = merged.slice(0, 10);
    }
    this.syncSelectedCoachEntry();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.reasoning-dropdown')) {
      this.reasoningDropdownOpen = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.ensurePopPlayInBounds();
    this.ensureFabInBounds();
  }

  startFabDrag(event: PointerEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startPos = this.getFabPosition();

    const onMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      this.setFabPosition(startPos.x + dx, startPos.y + dy);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  startPopPlayDrag(event: PointerEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startPos = this.getPopPlayPosition();

    const onMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      this.setPopPlayPosition(startPos.x + dx, startPos.y + dy);
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  private getFabPosition(): { x: number; y: number } {
    if (this.fabPosition) return this.fabPosition;
    const fallback = this.getDefaultFabPosition();
    this.fabPosition = fallback;
    return fallback;
  }

  private getPopPlayPosition(): { x: number; y: number } {
    if (this.popPlayPosition) return this.popPlayPosition;
    const fallback = this.getDefaultPopPlayPosition();
    this.popPlayPosition = fallback;
    return fallback;
  }

  private setFabPosition(x: number, y: number): void {
    const width = 64;
    const height = 64;
    const maxX = window.innerWidth - width - 12;
    const maxY = window.innerHeight - height - 12;
    const nextX = Math.max(12, Math.min(maxX, x));
    const nextY = Math.max(12, Math.min(maxY, y));
    this.fabPosition = { x: nextX, y: nextY };
    this.fabStyle = {
      left: `${nextX}px`,
      top: `${nextY}px`,
      right: 'auto',
      bottom: 'auto'
    };
  }

  private setPopPlayPosition(x: number, y: number): void {
    const size = this.getPopPlaySize();
    const maxX = window.innerWidth - size.width - 12;
    const maxY = window.innerHeight - size.height - 12;
    const nextX = Math.max(12, Math.min(maxX, x));
    const nextY = Math.max(12, Math.min(maxY, y));
    this.popPlayPosition = { x: nextX, y: nextY };
    this.popPlayStyle = {
      left: `${nextX}px`,
      top: `${nextY}px`,
      right: 'auto',
      bottom: 'auto'
    };
  }

  private ensurePopPlayInBounds(): void {
    if (!this.popPlayPosition) return;
    this.setPopPlayPosition(this.popPlayPosition.x, this.popPlayPosition.y);
  }

  private ensureFabInBounds(): void {
    if (!this.fabPosition) return;
    this.setFabPosition(this.fabPosition.x, this.fabPosition.y);
  }

  private getDefaultFabPosition(): { x: number; y: number } {
    const width = 64;
    const height = 64;
    const offset = 18;
    // ALWAYS DEFAULT TO BOTTOM-RIGHT
    switch (this.fabCorner) {
      case 'bottom-left':
        return { x: offset, y: window.innerHeight - height - offset };
      case 'top-right':
        return { x: window.innerWidth - width - offset, y: offset };
      case 'top-left':
        return { x: offset, y: offset };
      case 'bottom-right':
      default:
        return { x: window.innerWidth - width - offset, y: window.innerHeight - height - offset };
    }
  }

  private getDefaultPopPlayPosition(): { x: number; y: number } {
    const size = this.getPopPlaySize();
    return {
      x: window.innerWidth - size.width - 18,
      y: window.innerHeight - size.height - 18
    };
  }

  private getPopPlaySize(): { width: number; height: number } {
    if (this.popPlaySize === 'large') {
      return { width: 360, height: 260 };
    }
    return { width: 320, height: 210 };
  }
}
